import { 
  useCallback, 
  useEffect, 
  useRef, 
  useState 
} from 'react';

/**
 * A seconds-remaining ticker, used for the three deadlines these screens have to state rather
 * than hide: the 5-minute recovery ticket and sign-in ticket, the 10-minute mailed code, and
 * the 60-second resend cooldown that mirrors the server's per-address cooldown.
 *
 * Pass `0` to start idle. `restart` takes an optional new duration so one hook can serve a
 * resend that resets both the cooldown and the expiry.
 */
export const useCountdown = (initialSeconds = 0) => {
  const [secondsLeft, setSecondsLeft] = useState(initialSeconds);
  const deadlineRef = useRef<number>(
    initialSeconds > 0 ? Date.now() + initialSeconds * 1000 : 0
  );

  useEffect(() => {
    if (secondsLeft <= 0) return undefined;
    // Anchored on a wall-clock deadline rather than decrementing, so a backgrounded tab
    // does not come back showing a countdown that has drifted minutes behind the ticket.
    const id = setInterval(() => {
      const remaining = Math.max(
        0,
        Math.ceil((deadlineRef.current - Date.now()) / 1000)
      );
      setSecondsLeft(remaining);
    }, 1000);
    return () => clearInterval(id);
  }, [secondsLeft]);

  const restart = useCallback((seconds: number) => {
    deadlineRef.current = Date.now() + seconds * 1000;
    setSecondsLeft(seconds);
  }, []);

  const stop = useCallback(() => {
    deadlineRef.current = 0;
    setSecondsLeft(0);
  }, []);

  return {
    secondsLeft,
    isRunning: secondsLeft > 0,
    restart,
    stop
  };
};

/** `125` → `2:05`. */
export const formatCountdown = (seconds: number): string => {
  const safe = Math.max(0, seconds);
  const minutes = Math.floor(safe / 60);
  return `${minutes}:${String(safe % 60).padStart(2, '0')}`;
};

export default useCountdown;
